/**
 * Mantem um `AudioContext` rodando — e avisa quando nao consegue mais.
 *
 * Existe por causa de uma falha que nao da nenhum sinal. As trilhas de audio
 * que o LFRCORD publica na chamada (microfone com supressao de ruido, e o
 * audio do computador na transmissao) sao a saida de um
 * `createMediaStreamDestination()`. Se o `AudioContext` que alimenta esse
 * destino for suspenso, a trilha continua com `readyState: "live"` e
 * `muted: false` — ela apenas passa a emitir SILENCIO.
 *
 * Nenhum evento `ended`, nenhum erro, nenhuma mudanca na tela. As outras
 * pessoas simplesmente param de ouvir, "em algum momento", e quem esta falando
 * nao tem como saber.
 *
 * No Windows isso acontece por dois motivos comuns: trocar ou religar
 * dispositivo de audio, e o servico de audio do Chromium reiniciar — o que
 * abrir uma captura de loopback (o "incluir audio do computador" da
 * transmissao) costuma provocar. Elemento `<audio>` se recupera sozinho desses
 * dois; o `AudioContext`, nao.
 *
 * Fica em arquivo proprio, sem nenhum import de navegador, por duas razoes:
 * dois modulos precisam dele, e assim ele pode ser testado em Node com um
 * contexto de mentira — que e como a regra abaixo foi conferida.
 */

/** De quanto em quanto tempo conferimos se o contexto continua rodando. */
export const VIGIA_MS = 2_000;

/** Depois de tantas tentativas seguidas sem sucesso, o contexto nao volta. */
export const MAX_TENTATIVAS = 3;

/**
 * O minimo de `AudioContext` que o vigia usa.
 *
 * Tipado assim, e nao como `AudioContext`, para o teste conseguir passar um
 * contexto de mentira sem reimplementar a API inteira do navegador.
 */
export type ContextoVigiado = {
  readonly state: string;
  resume: () => Promise<void>;
  addEventListener: (tipo: "statechange", ouvinte: () => void) => void;
  removeEventListener: (tipo: "statechange", ouvinte: () => void) => void;
};

export type OpcoesDoVigia = {
  /** Chamado UMA vez, quando o contexto nao volta depois de `MAX_TENTATIVAS`. */
  aoDesistir?: () => void;
  /** Injetavel para o teste nao depender de tempo de verdade. */
  agendar?: (fn: () => void, ms: number) => number;
  cancelar?: (id: number) => void;
};

/**
 * Comeca a vigiar. Devolve a funcao que para a vigilancia.
 *
 * `statechange` sozinho nao basta: quando o servico de audio do sistema
 * reinicia, o evento as vezes nao chega. Por isso a checagem periodica ao lado
 * dele — uma leitura de string a cada dois segundos, que nao pesa em nada.
 */
export function manterContextoVivo(
  context: ContextoVigiado,
  opcoes: OpcoesDoVigia = {},
): () => void {
  const agendar = opcoes.agendar ?? ((fn, ms) => window.setInterval(fn, ms));
  const cancelar = opcoes.cancelar ?? (id => window.clearInterval(id));

  let vigiando = true;
  let falhas = 0;

  const conferir = (): void => {
    if (!vigiando) return;
    if (context.state === "running") {
      falhas = 0;
      return;
    }
    // Fechado e desfecho normal (o `dispose` de quem criou); nao ha o que
    // reanimar, e insistir chamaria `resume` num contexto morto para sempre.
    if (context.state === "closed") return;

    void context.resume().then(
      () => {
        falhas = 0;
      },
      () => {
        falhas += 1;
        if (falhas >= MAX_TENTATIVAS && vigiando) {
          // Para de vigiar antes de avisar: quem recebe o aviso vai refazer o
          // pipeline inteiro, e um segundo aviso no meio disso so atrapalha.
          vigiando = false;
          opcoes.aoDesistir?.();
        }
      },
    );
  };

  context.addEventListener("statechange", conferir);
  const timer = agendar(conferir, VIGIA_MS);
  conferir();

  return () => {
    vigiando = false;
    cancelar(timer);
    context.removeEventListener("statechange", conferir);
  };
}
